/**
 * imageOptimization.js
 * Utilities for optimizing images and improving loading performance
 */

import { debounce } from './codeOptimization';

/**
 * Configuration for image optimization
 */
const config = {
  lazyLoadThreshold: 200, // px before image enters viewport
  placeholderColor: '#e0e0e0',
  defaultQuality: 80,
  supportedFormats: ['webp', 'avif', 'jpeg', 'png'],
  sizeSuffixes: {
    small: '_sm',
    medium: '_md',
    large: '_lg',
    thumbnail: '_thumb'
  }
};

/**
 * Detects browser support for various image formats
 * @returns {Promise<Object>} - Object with support status for each format
 */
export const detectImageSupport = async () => {
  const formats = {
    webp: false,
    avif: false,
    jpeg: true,
    png: true
  };

  // Test WebP support
  const webpImage = new Image();
  webpImage.onload = () => { formats.webp = true; };
  webpImage.src = 'data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==';

  // Test AVIF support
  const avifImage = new Image();
  avifImage.onload = () => { formats.avif = true; };
  avifImage.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=';

  // Wait for image tests to complete
  await new Promise(resolve => setTimeout(resolve, 100));

  return formats;
};

/**
 * Gets the best supported image format for the current browser
 * @returns {Promise<string>} - The best supported format
 */
export const getBestImageFormat = async () => {
  const formats = await detectImageSupport();
  
  if (formats.avif) return 'avif';
  if (formats.webp) return 'webp';
  return 'jpeg';
};

/**
 * Generates a responsive image URL with the appropriate format and size
 * @param {string} baseUrl - The base URL of the image
 * @param {string} size - The desired size (small, medium, large, thumbnail)
 * @param {number} quality - The image quality (1-100)
 * @returns {Promise<string>} - The optimized image URL
 */
export const getResponsiveImageUrl = async (baseUrl, size = 'medium', quality = config.defaultQuality) => {
  if (!baseUrl) return '';
  
  const bestFormat = await getBestImageFormat();
  const sizeSuffix = config.sizeSuffixes[size] || '';
  
  // Extract base filename and extension
  const urlParts = baseUrl.split('.');
  const extension = urlParts.pop();
  const baseFilename = urlParts.join('.');
  
  // Construct new URL with format and size
  return `${baseFilename}${sizeSuffix}.${bestFormat}?q=${quality}`;
};

/**
 * Creates a placeholder image with specified dimensions and color
 * @param {number} width - The width of the placeholder
 * @param {number} height - The height of the placeholder
 * @param {string} color - The background color of the placeholder
 * @returns {string} - Data URL for the placeholder image
 */
export const createPlaceholderImage = (width = 100, height = 100, color = config.placeholderColor) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);
  
  return canvas.toDataURL('image/png');
};

/**
 * Lazy loads images when they approach the viewport
 * @param {string} selector - CSS selector for images to lazy load
 * @param {number} threshold - Distance in pixels before viewport to start loading
 */
export const setupLazyLoading = (selector = 'img[data-src]', threshold = config.lazyLoadThreshold) => {
  // Use IntersectionObserver if available
  if ('IntersectionObserver' in window) {
    const loadImage = (entry) => {
      const img = entry.target;
      const src = img.getAttribute('data-src');
      const srcset = img.getAttribute('data-srcset');
      
      if (src) img.src = src;
      if (srcset) img.srcset = srcset;
      
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.classList.add('loaded');
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          loadImage(entry);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: `${threshold}px` });
    
    document.querySelectorAll(selector).forEach(img => {
      observer.observe(img);
    });
  } else {
    // Fallback for browsers without IntersectionObserver
    const lazyLoadImages = () => {
      const images = document.querySelectorAll(selector);
      const viewportHeight = window.innerHeight;
      
      images.forEach(img => {
        const rect = img.getBoundingClientRect();
        
        if (rect.top - threshold <= viewportHeight && rect.bottom + threshold >= 0) {
          const src = img.getAttribute('data-src');
          const srcset = img.getAttribute('data-srcset');
          
          if (src) img.src = src;
          if (srcset) img.srcset = srcset;
          
          img.removeAttribute('data-src');
          img.removeAttribute('data-srcset');
          img.classList.add('loaded');
        }
      });
    };
    
    // Debounce scroll and resize events
    const debouncedLazyLoad = debounce(lazyLoadImages, 200);
    
    window.addEventListener('scroll', debouncedLazyLoad);
    window.addEventListener('resize', debouncedLazyLoad);
    window.addEventListener('orientationchange', debouncedLazyLoad);
    
    // Initial load
    lazyLoadImages();
  }
};

/**
 * Preloads critical images to improve perceived performance
 * @param {Array<string>} imageUrls - Array of image URLs to preload
 * @returns {Promise<void>} - Promise that resolves when all images are preloaded
 */
export const preloadCriticalImages = async (imageUrls = []) => {
  if (!imageUrls.length) return;
  
  const preloadPromises = imageUrls.map(url => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
  });
  
  try {
    await Promise.all(preloadPromises);
    console.log(`Preloaded ${imageUrls.length} critical images`);
  } catch (error) {
    console.warn('Error preloading images:', error);
  }
};

/**
 * Creates a responsive image component with srcset and sizes attributes
 * @param {string} baseUrl - The base URL of the image
 * @param {Object} sizes - Object with breakpoint sizes
 * @param {string} alt - Alt text for the image
 * @param {boolean} lazy - Whether to lazy load the image
 * @returns {HTMLImageElement} - The image element
 */
export const createResponsiveImage = (baseUrl, sizes = { sm: 400, md: 800, lg: 1200 }, alt = '', lazy = true) => {
  const img = document.createElement('img');
  img.alt = alt;
  
  // Create srcset
  const srcsetPromises = Object.entries(sizes).map(async ([size, width]) => {
    const url = await getResponsiveImageUrl(baseUrl, size);
    return `${url} ${width}w`;
  });
  
  // Set placeholder while loading
  img.src = createPlaceholderImage(100, 100);
  
  // When all srcset URLs are ready
  Promise.all(srcsetPromises).then(srcsetValues => {
    const srcset = srcsetValues.join(', ');
    
    if (lazy) {
      img.setAttribute('data-srcset', srcset);
      img.setAttribute('data-src', baseUrl);
      img.classList.add('lazy');
    } else {
      img.srcset = srcset;
      img.src = baseUrl;
    }
  });
  
  // Set sizes attribute
  const sizesAttr = Object.entries(sizes)
    .map(([size, width]) => {
      if (size === 'lg') return `${width}px`;
      return `(max-width: ${width}px) ${width}px`;
    })
    .join(', ');
  
  img.sizes = sizesAttr;
  
  return img;
};

/**
 * Compresses an image file before upload
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<Blob>} - The compressed image blob
 */
export const compressImageForUpload = async (file, options = {}) => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8,
    format = 'jpeg'
  } = options;
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // Create canvas and compress image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => resolve(blob),
          `image/${format}`,
          quality
        );
      };
      
      img.onerror = reject;
    };
    
    reader.onerror = reject;
  });
};

/**
 * Default export with all image optimization utilities
 */
export default {
  getResponsiveImageUrl,
  createPlaceholderImage,
  setupLazyLoading,
  preloadCriticalImages,
  createResponsiveImage,
  compressImageForUpload,
  detectImageSupport,
  getBestImageFormat,
  config
};